"use client";

import { useCallback, useEffect, useState, useSyncExternalStore } from "react";

import { applyConsent } from "@/integrations/posthog/consent";
import { applyShopifyConsent } from "@/integrations/shopify/customer-privacy";

import {
  CONSENT_CHANGE_EVENT,
  CONSENT_STORAGE_KEY,
  LEGACY_CONSENT_STORAGE_KEY,
  type ConsentState,
  type ConsentValue,
  getConsent,
  setConsent,
} from "./consent-storage";

interface UseConsentResult {
  // `null` means "no decision yet — show the banner".
  state: ConsentState | null;
  // Per-category shortcuts; `null` while no decision has been made.
  analytics: ConsentValue | null;
  marketing: ConsentValue | null;
  // `false` during SSR and the first client render; flips to `true` after
  // hydration so callers can avoid flashing UI before localStorage is read.
  ready: boolean;
  // Persist an explicit per-category decision (used by the settings modal).
  save: (next: ConsentState) => void;
  // Banner shortcuts: accept or decline every category at once.
  grantAll: () => void;
  denyAll: () => void;
}

function subscribe(notify: () => void): () => void {
  window.addEventListener(CONSENT_CHANGE_EVENT, notify);
  // Cross-tab updates: storage events fire in *other* tabs.
  const handleStorage = (event: StorageEvent) => {
    if (
      event.key === CONSENT_STORAGE_KEY ||
      event.key === LEGACY_CONSENT_STORAGE_KEY ||
      event.key === null
    ) {
      notify();
    }
  };
  window.addEventListener("storage", handleStorage);
  return () => {
    window.removeEventListener(CONSENT_CHANGE_EVENT, notify);
    window.removeEventListener("storage", handleStorage);
  };
}

const getServerSnapshot = (): ConsentState | null => null;

export function useConsent(): UseConsentResult {
  const state = useSyncExternalStore(subscribe, getConsent, getServerSnapshot);
  // `useSyncExternalStore` matches the SSR snapshot on the first client
  // render to avoid hydration mismatches; we flip `ready` after that so the
  // banner can render without flashing for users with a persisted choice.
  // The setState-in-effect is intentional and the documented React pattern
  // for hydration-aware UI; no replacement primitive expresses it.
  const [ready, setReady] = useState(false);
  useEffect(() => {
    // eslint-disable-next-line react-hooks/set-state-in-effect
    setReady(true);
  }, []);

  // Replay the persisted decision to Shopify once per session. Without this, a
  // visitor who consented on an earlier visit never calls `save` again, so
  // Shopify would hold no consent state by the time they reach checkout and
  // every pixel there would stay blocked. `state` is referentially stable
  // between renders (see the cache in `getConsent`), so this does not re-fire
  // on unrelated re-renders.
  useEffect(() => {
    if (!state) {
      return;
    }
    void applyShopifyConsent(state);
  }, [state]);

  const save = useCallback((next: ConsentState) => {
    setConsent(next);
    // Only the analytics SDK (PostHog) is wired here. The marketing pixels
    // react to the marketing category on their own (see `TikTokPixel`,
    // `MetaPixel`, `GoogleAdsTag`) so this hook stays free of a
    // marketing-integration dependency.
    applyConsent(next.analytics);
    // Shopify is the deliberate exception to that rule: nothing running inside
    // Shopify checkout can observe our state on its own, so the decision has to
    // be pushed across. Both categories go, because Shopify gates analytics and
    // marketing pixels separately. Fire-and-forget by contract — a failure is a
    // measurement gap, never a reason to block the banner.
    void applyShopifyConsent(next);
  }, []);

  const grantAll = useCallback(() => {
    save({ analytics: "granted", marketing: "granted" });
  }, [save]);

  const denyAll = useCallback(() => {
    save({ analytics: "denied", marketing: "denied" });
  }, [save]);

  return {
    state,
    analytics: state?.analytics ?? null,
    marketing: state?.marketing ?? null,
    ready,
    save,
    grantAll,
    denyAll,
  };
}
